/* Hämtning av dokument för förhandsvisning – utbrutet ur DocumentPreviewModal
   (2026-10-23, Mikael F14) så att samma visning kan ligga i en dialog eller i
   en högerpanel (KanAI:s källpanel). Beteendet är oförändrat. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { API_URL, apiFetch } from '../../api';
import { resolvePreviewKind, createSafePreviewUrl } from '../../utils/filePreview';

// Extensions that the APS Viewer can handle (subset most relevant to construction)
export const APS_EXTENSIONS = new Set([
  'dwg', 'dxf', 'dwf', 'dwfx',
  'rvt', 'rfa', 'rte',
  'ifc',
  'nwd', 'nwc', 'nwf',
  'stp', 'step', 'igs', 'iges',
  '3dm', 'obj', 'stl', 'fbx',
  'sat', 'sab',
  'catpart', 'catproduct',
  'sldprt', 'sldasm',
  'f3d',
]);

/** Extract project_id + document_id from a typical KanPlan download URL. */
export function parseIdsFromUrl(url) {
  if (!url) return null;
  const m = String(url).match(/\/projects\/(\d+)\/documents\/([^/]+)/);
  if (m) return { projectId: m[1], documentId: m[2] };
  return null;
}

export function toAbsoluteApiUrl(rawUrl) {
  if (!rawUrl) return '';

  // Relative -> join against API_URL
  if (!rawUrl.startsWith('http://') && !rawUrl.startsWith('https://')) {
    return `${API_URL}${rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`}`;
  }

  // Absolute -> if page is HTTPS, never keep HTTP (Mixed Content)
  try {
    const parsed = new URL(rawUrl);
    if (typeof window !== 'undefined' && window.location?.protocol === 'https:' && parsed.protocol === 'http:') {
      parsed.protocol = 'https:';
    }
    return parsed.toString();
  } catch {
    // If URL parsing fails, fall back to raw string
    return rawUrl;
  }
}

export function extFromName(name) {
  if (!name) return '';
  const clean = String(name).trim();
  const last = clean.split('/').pop() || clean;
  if (!last.includes('.')) return '';
  return (last.split('.').pop() || '').toLowerCase();
}

export function extFromUrl(u) {
  if (!u) return '';
  try {
    const { pathname } = new URL(u, window.location.href);
    const file = pathname.split('/').pop() || '';
    if (file.includes('.')) return (file.split('.').pop() || '').toLowerCase();
  } catch {
    const clean = String(u).split(/[?#]/)[0];
    const file = clean.split('/').pop() || clean;
    if (file.includes('.')) return (file.split('.').pop() || '').toLowerCase();
  }
  return '';
}

/**
 * Hämtar dokumentet medan open är sant och returnerar allt visningen behöver.
 * @param {boolean} open
 * @param {object|null} document
 */
export default function useDocumentPreview(open, document) {
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  /* Sort som förhandsvisningen FÅR renderas som – kommer ur vitlistan i
     utils/filePreview, aldrig ur den lagrade/serverskickade MIME-typen. */
  const [previewKind, setPreviewKind] = useState('');
  const [ifcFile, setIfcFile] = useState(null);
  const [markeradSida, setMarkeradSida] = useState(null); // X-Highlight-Page från servern
  const objectUrlRef = useRef('');
  const [apsConfigured, setApsConfigured] = useState(null); // null = unknown, true/false

  // Check once whether APS is configured on the backend
  useEffect(() => {
    let cancelled = false;
    apiFetch(`${API_URL}/aps/supported-formats`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setApsConfigured(d?.configured === true); })
      .catch(() => { if (!cancelled) setApsConfigured(false); });
    return () => { cancelled = true; };
  }, []);

  const preferredSourceUrl = useMemo(() => {
    const downloadUrl = document?.download_url || '';
    const name = document?.name || '';

    const byName = extFromName(name);
    const byUrl = extFromUrl(downloadUrl ? toAbsoluteApiUrl(downloadUrl) : '');
    const ext = (byName || byUrl || '').toLowerCase();

    if (['dwg', 'dxf'].includes(ext)) {
      const svgPreview = document?.previews?.svg?.previewUrl;
      if (svgPreview) return svgPreview;

      // If we have a standard download URL, derive preview.svg from it.
      // Expected shape: /projects/{pid}/documents/{did}/download
      if (downloadUrl && /\/download(\?.*)?$/i.test(downloadUrl)) {
        return downloadUrl.replace(/\/download(\?.*)?$/i, '/preview.svg$1');
      }

      // Fallback to PDF preview when SVG isn't available.
      const pdfPreview = document?.previews?.pdf?.previewUrl;
      if (pdfPreview) return pdfPreview;
    }

    return downloadUrl;
  }, [document]);

  useEffect(() => {
    if (!open || !document || !preferredSourceUrl) {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = '';
      }
      setPreviewUrl('');
      setLoading(false);
      setError('');
      setPreviewKind('');
      setIfcFile(null);
      return;
    }

    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError('');
      setPreviewKind('');
      setIfcFile(null);
      setMarkeradSida(null);
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = '';
      }
      try {
        const ext = (() => {
          const byName = extFromName(document?.name || '');
          if (byName) return byName;
          const absoluteUrl = preferredSourceUrl ? toAbsoluteApiUrl(preferredSourceUrl) : '';
          return extFromUrl(absoluteUrl);
        })();

        const absolute = toAbsoluteApiUrl(preferredSourceUrl);
        // Include tenant header for management-system routes
        const tenantSlug = (() => {
          try { return String(localStorage.getItem('kanplan-active-tenant-slug') || '').trim(); }
          catch { return ''; }
        })();
        const res = await fetch(absolute, {
          credentials: 'include',
          signal: controller.signal,
          headers: tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {},
        });
        if (!res.ok) {
          let detail = '';
          try {
            const clone = res.clone();
            try {
              const data = await clone.json();
              detail = (data && (data.detail || data.message || data.error || data.hint)) ?
                String(data.detail || data.message || data.error || data.hint) : '';
            } catch {
              try {
                detail = String(await clone.text());
              } catch {}
            }
          } catch {}
          const base = `Kunde inte hämta dokument (${res.status})`;
          throw new Error(detail ? `${base}: ${detail}` : base);
        }
        /* KanAI:s markerings-PDF anger sidan där markeringen hamnade, som kan
           vara grannsidan till källans sida. Valfritt huvud: saknas det eller
           är det inte ett rimligt sidnummer gäller document.page som förut.
           PdfViewer begränsar sidan till dokumentets sidantal. */
        const hp = Number(res.headers.get('X-Highlight-Page'));
        setMarkeradSida(Number.isInteger(hp) && hp >= 1 && hp <= 5000 ? hp : null);
        const blob = await res.blob();
        const serverContentType = blob.type || res.headers.get('content-type') || '';
        const lowerExt = String(ext || '').toLowerCase();
        if (lowerExt === 'ifc') {
          /* IFC parsas av ViewerCanvas, aldrig av webbläsaren -> neutral typ. */
          setIfcFile(new File([blob], String(document?.name || 'model.ifc'), { type: 'application/octet-stream' }));
          setPreviewUrl('');
          setPreviewKind('ifc');
        } else {
          /* DWG/DXF hämtas som serverrenderad SVG och saneras i DwgSvgViewer. */
          const resolved = ['dwg', 'dxf'].includes(lowerExt)
            ? { kind: 'dwg', mime: 'image/svg+xml' }
            : resolvePreviewKind({
              filename: document?.name || '',
              url: absolute,
              contentType: serverContentType,
            });
          const objUrl = createSafePreviewUrl(blob, resolved.mime);
          objectUrlRef.current = objUrl;
          setPreviewUrl(objUrl);
          setPreviewKind(resolved.kind);
        }
      } catch (e) {
        if (e.name === 'AbortError') return;
        setError(e.message || 'Kunde inte förhandsvisa dokumentet');
        setPreviewUrl('');
        setPreviewKind('');
        setIfcFile(null);
      } finally {
        setLoading(false);
      }
    };

    load();

    return () => {
      controller.abort();
    };
  }, [open, document, preferredSourceUrl]);

  useEffect(() => () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = '';
    }
  }, []);

  const name = document?.name || '';
  const downloadUrl = document?.download_url || '';
  const safeName = name || 'Dokument';
  const downloadUrlAbsolute = downloadUrl ? toAbsoluteApiUrl(downloadUrl) : '';
  const openInNewTabUrlAbsolute = preferredSourceUrl ? toAbsoluteApiUrl(preferredSourceUrl) : downloadUrlAbsolute;

  const effectiveExt = useMemo(() => {
    const byName = extFromName(name);
    if (byName) return byName;
    return extFromUrl(downloadUrlAbsolute);
  }, [name, downloadUrlAbsolute]);

  return {
    previewUrl, loading, error, setError, previewKind, ifcFile, markeradSida, apsConfigured,
    safeName, downloadUrlAbsolute, openInNewTabUrlAbsolute, effectiveExt,
  };
}
